import { createSignal, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { api } from "@/lib/api";
import { sharedApi } from "@boveda/shared";
import { msg } from "@lingui/core/macro";
import { CheckCircle2, Loader2, MailWarning, Send } from "lucide-solid";

/**
 * Aviso de email sin verificar con reenvío del email de verificación.
 * Solo se renderiza si el usuario autenticado tiene `emailVerified: false`;
 * el estado verificado no muestra nada (sin ruido en el perfil).
 */
export function EmailVerificationBanner(props: { emailVerified: boolean | undefined }) {
  const { t } = useI18n();
  const [sending, setSending] = createSignal(false);
  const [sent, setSent] = createSignal(false);

  const resend = () => {
    void (async () => {
      setSending(true);
      try {
        const res = await sharedApi.sendVerificationEmail(api);
        if (!res.error) {
          setSent(true);
        }
      } finally {
        setSending(false);
      }
    })();
  };

  return (
    <Show when={props.emailVerified === false}>
      <div
        class="flex items-start gap-3 rounded-xl border border-warning/20 bg-warning/5 p-4"
        data-testid="email-unverified-banner"
      >
        <MailWarning class="mt-0.5 h-5 w-5 text-warning" />
        <div class="flex-1 space-y-2">
          <div class="space-y-1">
            <p class="text-sm font-bold text-warning">{t(msg`Email not verified`)}</p>
            <p class="text-xs text-muted-foreground">
              {t(msg`Check your inbox for the verification link. The link expires after 24 hours.`)}
            </p>
          </div>
          <Show
            when={!sent()}
            fallback={
              <p
                class="flex items-center gap-2 text-xs font-medium text-primary"
                data-testid="verification-email-sent"
              >
                <CheckCircle2 class="h-4 w-4" />
                {t(msg`Verification email sent. Check your inbox.`)}
              </p>
            }
          >
            <Button
              variant="outline"
              size="sm"
              onClick={resend}
              disabled={sending()}
              data-testid="resend-verification-button"
            >
              <Show when={!sending()} fallback={<Loader2 class="mr-2 h-4 w-4 animate-spin" />}>
                <Send class="mr-2 h-4 w-4" />
              </Show>
              {t(msg`Resend verification email`)}
            </Button>
          </Show>
        </div>
      </div>
    </Show>
  );
}
